import React, {FC, useEffect, useMemo, useRef, useState} from 'react'
import InfoTooltip from './InfoTooltip'
import {useApplyPlatformSettingShortcodes} from 'src/utils/usePlatformSettingDisplayText'

type TimeRangePickerInputProps = {
  label: string
  message: string
  name: string
  id: string
  value: string
  placeholder?: string
  required?: boolean
  onChange: (e: React.ChangeEvent<any>) => void
  containerClasses?: string
  hideLabel?: boolean
  sourceChannelName?: string | null
}

const HOURS = Array.from({length: 24}, (_, index) => String(index).padStart(2, '0'))

const parseRange = (rawValue?: string): {start: string; end: string} => {
  const value = String(rawValue ?? '').trim()
  const fallback = {start: '11', end: '16'}
  if (!value) {
    return fallback
  }

  const matches = value.match(/(\d{1,2}):(\d{2})\s*-\s*(\d{1,2}):(\d{2})/)
  if (!matches) {
    return fallback
  }

  const start = String(Number(matches[1])).padStart(2, '0')
  const end = String(Number(matches[3])).padStart(2, '0')

  if (!HOURS.includes(start) || !HOURS.includes(end)) {
    return fallback
  }

  return {start, end}
}

const normalizeRangeValue = (startHour: string, endHour: string): string => {
  return `${startHour}:00 - ${endHour}:00`
}

const TimeRangePickerInput: FC<TimeRangePickerInputProps> = ({
  label,
  message,
  name,
  id,
  value,
  placeholder = '',
  required = false,
  onChange,
  containerClasses = 'col-12 col-sm-6 mt-4',
  hideLabel = false,
  sourceChannelName,
}) => {
  const formatSettingDisplayText = useApplyPlatformSettingShortcodes(sourceChannelName)
  const displayLabel = formatSettingDisplayText(label)
  const displayPlaceholder = formatSettingDisplayText(placeholder)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const initialRange = useMemo(() => parseRange(value), [value])
  const [isOpen, setIsOpen] = useState(false)
  const [startHour, setStartHour] = useState(initialRange.start)
  const [endHour, setEndHour] = useState(initialRange.end)

  useEffect(() => {
    const parsed = parseRange(value)
    setStartHour(parsed.start)
    setEndHour(parsed.end)
  }, [value])

  useEffect(() => {
    if (!isOpen) {
      return
    }

    const handleOutsideClick = (event: MouseEvent) => {
      if (!wrapperRef.current?.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleOutsideClick)
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick)
    }
  }, [isOpen])

  const normalizedDisplayValue = value?.trim()
    ? value
    : normalizeRangeValue(startHour, endHour)
  const isValidRange = Number(startHour) < Number(endHour)

  const emitValue = (nextValue: string) => {
    onChange({
      target: {
        name,
        id,
        value: nextValue,
      },
    } as React.ChangeEvent<any>)
  }

  const onConfirm = () => {
    if (!isValidRange) {
      return
    }
    emitValue(normalizeRangeValue(startHour, endHour))
    setIsOpen(false)
  }

  return (
    <div className={containerClasses} style={{paddingRight: '5.5rem'}}>
      {!hideLabel ? (
        <label className={`form-label ${required ? 'required' : ''}`} htmlFor={id}>
          {displayLabel} &nbsp;&nbsp;
          <InfoTooltip message={message} sourceChannelName={sourceChannelName} />
        </label>
      ) : null}
      <div className='time-range-picker position-relative' ref={wrapperRef}>
        <button
          type='button'
          className='form-control form-control-lg text-start d-flex align-items-center justify-content-between'
          onClick={() => setIsOpen((prev) => !prev)}
          aria-label={displayLabel}
        >
          <span>{normalizedDisplayValue || displayPlaceholder}</span>
          <i className='bi bi-clock text-muted' />
        </button>
        {isOpen ? (
          <div className='time-range-picker__panel shadow-sm border rounded bg-white p-3'>
            <div className='d-flex gap-3'>
              <div className='flex-fill'>
                <div className='fw-semibold mb-2 small'>Start Time</div>
                <select
                  className='form-select form-select-sm'
                  value={startHour}
                  onChange={(event) => setStartHour(event.target.value)}
                  size={8}
                >
                  {HOURS.map((hour) => (
                    <option key={`start-${hour}`} value={hour}>
                      {hour}:00
                    </option>
                  ))}
                </select>
              </div>
              <div className='flex-fill'>
                <div className='fw-semibold mb-2 small'>End Time</div>
                <select
                  className='form-select form-select-sm'
                  value={endHour}
                  onChange={(event) => setEndHour(event.target.value)}
                  size={8}
                >
                  {HOURS.map((hour) => (
                    <option key={`end-${hour}`} value={hour}>
                      {hour}:00
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {!isValidRange ? (
              <div className='text-danger mt-2 small'>End time must be greater than start time.</div>
            ) : null}
            <div className='d-flex justify-content-end mt-3'>
              <button
                type='button'
                className='btn btn-sm btn-primary'
                onClick={onConfirm}
                disabled={!isValidRange}
              >
                Confirm
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

export default TimeRangePickerInput
